import React from 'react';

import {Source} from 'constants/SourceConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import NavStore from 'nav/NavStore';
import {ButtonSize} from 'ui-library/buttons/Button';

import SourceSelector from './SourceSelector';
interface BaseProps {
  buttonSize?: ButtonSize;
  selectedProjectId: string | null;
  readOnly?: boolean;
  level?: IntentLevel;
  enableRedesign: boolean;
  /** Key where last selected project ID will be saved to. */
  preferenceKey?: string;
  /**
   * If the project selector modal is opened from a tooltip, its z-index may need to be raised to render
   * on top of that tooltip.
   *
   * @default 1040
   */
  selectorModalZIndex?: 1040 | 1060;
}

type NonNullableProjectSelectorProps = BaseProps & {
  allowNone?: false;
  onProjectSelected: (projectId: string) => void;
};

type NullableProjectSelectorProps = BaseProps & {
  allowNone: true;
  onProjectSelected: (projectId: string | null) => void;
};

export type BaseProjectSelectorProps = NonNullableProjectSelectorProps | NullableProjectSelectorProps;

export type ProjectSelectorProps = BaseProjectSelectorProps & {
  areSourcesLoading?: boolean;
  sources: Array<Source>;
};

/**
 * ProjectSelector that renders throbber if projects are still loading
 */
export function UnconnectedProjectSelector(props: ProjectSelectorProps): JSX.Element {
  return (
    <SourceSelector
      areSourcesLoading={props.areSourcesLoading}
      buttonSize={props.buttonSize}
      selectedSourceId={props.selectedProjectId}
      showRegistries={false}
      sources={props.sources}
      readOnly={props.readOnly}
      level={props.level}
      enableRedesign={props.enableRedesign}
      preferenceKey={props.preferenceKey}
      selectorModalZIndex={props.selectorModalZIndex}
      allowNone={props.allowNone}
      onSourceSelected={props.onProjectSelected}
    />
  );
}

/**
 * Connected ProjectSelector that reads projects from NavStore
 *
 * Like most of the app, it assumes the projects are already loaded in NavStore, via FileStates's call to
 * FileBrowserActions.fetchSources(). We avoid calling the fetch here to avoid duplicate fetches (projects
 * aren't managed models).
 */
export default NavStore.connect((navStore: typeof NavStore, props: BaseProjectSelectorProps) => ({
  ...props,
  areSourcesLoading: navStore.isLoading(),
  sources: navStore.getSources() ?? [],
}))(UnconnectedProjectSelector);
